/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable no-param-reassign */
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GetAppIcon from "@mui/icons-material/GetApp";
import InfoIcon from "@mui/icons-material/Info";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import TuneIcon from "@mui/icons-material/Tune";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { usePopup } from "@nodeon/react-components";
import * as Cesium from "cesium";
import RulerSquareIcon from "mdi-react/RulerSquareIcon";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Choose, If } from "react-extras";
import { useTranslation } from "react-i18next";
import { ReturnNodeDto, useSeaSurveyApi } from "~/api/GeneratedApi";
import SeaSurveyDialog from "~/pages/marine-survey-and-design-page/dialogs/SeaSurveyDialog";
import {
  setSelectedPointcloud,
  toggleCesiumPrimitivesChanged,
} from "~/redux/reducers/mapReducer";
import { useAppDispatch, useAppSelector } from "~/redux/store/store";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { getTileset } from "../functions/getTileset";
import { removeTileset } from "../functions/removeTileset";
import { useCesiumTilesetLoader } from "../hooks/useCesiumTilesetLoader";
import {
  CesiumMeasurementItem,
  Custom3DTileset,
} from "../types/CesiumMapItems";
import { DrawerItem } from "./CesiumDrawerDataItems";

const useStyles = makeStyles((theme) => ({
  item: {
    backgroundColor: theme.palette.primary.semiLight,
    color: theme.palette.primary.contrastText,
    width: "100%",
  },
  itemHeading: {
    margin: theme.spacing(1),
    wordBreak: "break-all",
  },
  icon: {
    color: theme.palette.primary.contrastText,
  },
  loadingIndicator: {
    margin: `auto ${theme.spacing(1)}`,
    color: theme.palette.primary.contrastText,
  },
  summaryContent: {
    alignItems: "center",
  },
}));

interface Props {
  item: DrawerItem<CesiumMeasurementItem>;
  viewer: Cesium.Viewer;
  hovered: boolean;
}

type TilesetShowing = {
  nodeId: number;
  showing: boolean;
};

type TilesetLoaded = {
  nodeId: number;
  loaded: boolean;
};

const CesiumDrawerDataItem = ({
  item,
  viewer,
  hovered,
}: Props): JSX.Element => {
  const classes = useStyles();
  const { t } = useTranslation("cesium");
  const [markerShowing, setMarkerShowing] = useState(false);
  const [tilesetsShowing, setTilesetsShowing] = useState<TilesetShowing[]>();
  const [tilesetsLoaded, setTilesetsLoaded] = useState<TilesetLoaded[]>();
  const tilesetRefs = useRef<Cesium.Cesium3DTileset[]>();
  const dispatch = useAppDispatch();
  const { show } = usePopup();
  const { getSeaSurvey } = useSeaSurveyApi();
  const [seaSurveyToViewId, setSeaSurveyToViewId] = React.useState<number>();
  const primitivesChanged = useAppSelector(
    (s) => s.map.cesiumPrimitivesChanged,
  );
  const loadingIds = useAppSelector((s) => s.map.loadingTilesets);

  const { loader } = useCesiumTilesetLoader();

  useCustomQuery(
    { key: "sea-survey", id: seaSurveyToViewId },
    () => getSeaSurvey(seaSurveyToViewId!),
    {
      enabled: !!seaSurveyToViewId,
      refetchOnWindowFocus: false,
      onSuccess: (d) => {
        show(
          ({ close }) => (
            <SeaSurveyDialog
              onClose={() => {
                close();
                setSeaSurveyToViewId(undefined);
              }}
              onAdd={() => {
                close();
                setSeaSurveyToViewId(undefined);
                return null;
              }}
              onEdit={() => {
                close();
                setSeaSurveyToViewId(undefined);
                return null;
              }}
              activeAction="view"
              seaSurvey={d}
            />
          ),
          { noDialog: true },
        );
      },
    },
  );

  // Update tileset info
  useEffect(() => {
    const showing: TilesetShowing[] = [];
    const loaded: TilesetLoaded[] = [];
    const sets: Cesium.Cesium3DTileset[] = [];

    item.mapItem.files?.forEach((f) => {
      const ts = getTileset(f.id, viewer);
      showing.push({
        nodeId: f.id,
        showing: ts?.show ?? false,
      });
      loaded.push({
        nodeId: f.id,
        loaded: !!ts ?? false,
      });
      if (ts) {
        sets.push(ts);
      }
    });
    tilesetRefs.current = sets;
    setTilesetsLoaded(loaded);
    setTilesetsShowing(showing);
  }, [item.mapItem.files, primitivesChanged, viewer]);

  // Show expand for all items (tilesets)
  const showExpand = useMemo(
    () => !!item.mapItem.files?.length,
    [item?.mapItem?.files],
  );

  // True if marker is showing, or if any tileset is showing
  const isShowing = useMemo(
    () =>
      tilesetRefs.current?.length
        ? tilesetsShowing?.some((ts) => ts.showing)
        : markerShowing,
    [markerShowing, tilesetsShowing],
  );

  const updateEntityState = useCallback((entity: Cesium.Entity) => {
    setMarkerShowing(entity.isShowing);
  }, []);

  // Listen to changes in entity and update thing accordingly
  useEffect(() => {
    const listener = () => {
      updateEntityState(item.entity);
    };
    const removeListener =
      item.entity.definitionChanged.addEventListener(listener);

    updateEntityState(item.entity);
    return () => {
      removeListener();
    };
  }, [item, updateEntityState]);

  const handleVisibilityClick = (newVal: boolean, fileId?: number) => {
    if (!tilesetRefs.current?.length) {
      item.entity.show = newVal;
      return;
    }
    if (fileId) {
      const ts = tilesetRefs.current.find(
        (tile) => (tile as Custom3DTileset).fileId === fileId,
      );
      if (!ts) return;
      ts.show = newVal;
    } else {
      // Loop through all
      item.mapItem.files?.forEach((f) => {
        const ts = tilesetRefs.current?.find(
          (tile) => (tile as Custom3DTileset).fileId === f.id,
        );
        if (ts) ts.show = newVal;
      });
    }
    dispatch(toggleCesiumPrimitivesChanged());
  };

  const handleGoToClick = (fileId?: number) => {
    if (fileId) {
      const ts = tilesetRefs.current?.find(
        (tile) => (tile as Custom3DTileset).fileId === fileId,
      );
      if (!ts) return;
      viewer.zoomTo(ts ?? item.entity);
    } else if (markerShowing) {
      viewer.zoomTo(item.entity);
    } else if (tilesetRefs.current?.[0]) {
      viewer.zoomTo(tilesetRefs.current[0]);
    }
  };

  const handleDownloadClick = (file?: ReturnNodeDto) => {
    if (file) {
      loader({
        file,
        markerMapId: item.mapItem.mapId,
        viewer,
        projection: item.mapItem.coordinateSystem?.name ?? "",
        seaSurvey: item.mapItem,
      });
    } else {
      // Loop through all
      item.mapItem.files?.forEach((f) => {
        loader({
          file: f,
          markerMapId: item.mapItem.mapId,
          viewer,
          projection: item.mapItem.coordinateSystem?.name ?? "",
          seaSurvey: item.mapItem,
        });
      });
    }
  };

  const handleRemoveClick = (fileId?: number) => {
    if (!tilesetRefs.current) return;
    if (fileId) {
      const success = removeTileset(fileId, viewer);
      if (success) {
        dispatch(toggleCesiumPrimitivesChanged());
        // Show marker if no tilesets are loaded
        if (tilesetsLoaded?.every((ts) => !ts.loaded)) {
          item.entity.show = true;
        }
      }
    } else {
      // Loop through all
      item.mapItem.files?.forEach((f) => {
        const success = removeTileset(f.id, viewer);
        if (success) {
          // Show marker since all tilesets were removed
          item.entity.show = true;
          dispatch(toggleCesiumPrimitivesChanged());
        }
      });
    }
  };

  const handleAdjustClick = (fileId?: number) => {
    if (fileId) {
      if (
        !tilesetRefs.current?.find(
          (ts) => (ts as Custom3DTileset).fileId === fileId,
        )
      ) {
        return;
      }
      dispatch(setSelectedPointcloud([fileId]));
    } else {
      const ids: number[] = [];
      item.mapItem.files?.forEach((f) => {
        const tile = tilesetRefs.current?.find(
          (ts) => (ts as Custom3DTileset).fileId === f.id,
        );
        if (tile) ids.push(f.id);
      });
      if (ids.length) dispatch(setSelectedPointcloud(ids));
    }
  };

  const loading = useMemo(
    () =>
      item.mapItem.files
        ? item.mapItem.files?.some((f) => loadingIds?.includes(f.id))
        : false,
    [item.mapItem.files, loadingIds],
  );

  const allFilesLoaded = useMemo(
    () =>
      item.mapItem.files?.every(
        (f) => tilesetsLoaded?.find((ts) => ts.nodeId === f.id)?.loaded,
      ),
    [item.mapItem.files, tilesetsLoaded],
  );

  return (
    // <ListItem>
    <Accordion
      expanded={showExpand ? undefined : false}
      className={classes.item}
      elevation={hovered ? 6 : 1}
      TransitionProps={{ unmountOnExit: true }}
      disableGutters
      square
    >
      <AccordionSummary
        style={{ cursor: showExpand ? undefined : "default" }}
        classes={{
          expandIconWrapper: showExpand ? classes.icon : "",
          content: classes.summaryContent,
        }}
        expandIcon={showExpand ? <ExpandMoreIcon /> : null}
      >
        <Grid container justifyContent="center">
          <Grid
            item
            container
            xs
            flexDirection="row"
            alignItems="center"
            flexWrap="nowrap"
            sx={{ flexBasis: "100%" }}
          >
            <Grid item sx={{ mr: 1 }}>
              <Tooltip title={t("marine-survey").toString()}>
                <Avatar
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: (theme) => theme.palette.primary.contrastText,
                  }}
                >
                  <RulerSquareIcon />
                </Avatar>
              </Tooltip>
            </Grid>
            <Grid item>
              <Typography className={classes.itemHeading}>
                {item.mapItem.name}
              </Typography>
            </Grid>
          </Grid>
          <Tooltip title={t("info").toString()}>
            <Grid>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSeaSurveyToViewId(item.mapItem.id);
                }}
                size="large"
              >
                <InfoIcon className={classes.icon} />
              </IconButton>
            </Grid>
          </Tooltip>
          <Tooltip title={t("move-to").toString()}>
            <Grid>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleGoToClick();
                }}
                size="large"
              >
                <MyLocationIcon className={classes.icon} />
              </IconButton>
            </Grid>
          </Tooltip>
          {isShowing ? (
            <Tooltip title={t("hide-all").toString()}>
              <Grid>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVisibilityClick(false);
                  }}
                  size="large"
                >
                  <VisibilityOffIcon className={classes.icon} />
                </IconButton>
              </Grid>
            </Tooltip>
          ) : (
            <Tooltip title={t("show-all").toString()}>
              <Grid>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVisibilityClick(true);
                  }}
                  size="large"
                >
                  <VisibilityIcon className={classes.icon} />
                </IconButton>
              </Grid>
            </Tooltip>
          )}
          <Choose>
            <Choose.When
              condition={!loading && !allFilesLoaded}
              render={() => (
                <Tooltip title={t("download-all").toString()}>
                  <Grid>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadClick();
                      }}
                      size="large"
                    >
                      <GetAppIcon className={classes.icon} />
                    </IconButton>
                  </Grid>
                </Tooltip>
              )}
            />
            <Choose.When
              condition={loading}
              render={() => (
                <Grid>
                  <CircularProgress
                    size={24}
                    className={classes.loadingIndicator}
                  />
                </Grid>
              )}
            />
            <Choose.When
              condition={!loading}
              render={() => (
                <>
                  <Tooltip title={t("remove-from-map-all").toString()}>
                    <Grid>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveClick();
                        }}
                        size="large"
                      >
                        <CloseIcon className={classes.icon} />
                      </IconButton>
                    </Grid>
                  </Tooltip>
                  <Tooltip title={t("adjust-pointcloud-all").toString()}>
                    <Grid>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAdjustClick();
                        }}
                        size="large"
                      >
                        <TuneIcon className={classes.icon} />
                      </IconButton>
                    </Grid>
                  </Tooltip>
                </>
              )}
            />
          </Choose>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <List style={{ maxWidth: "100%", width: "100%" }}>
          {item.mapItem.files?.map((f, index) => {
            const tsLoading = loadingIds?.includes(f.id);
            return (
              <ListItem
                key={f.id}
                divider={
                  item.mapItem.files ? index < item.mapItem.files.length : false
                }
                disableGutters
                style={{ paddingLeft: 10, width: "100%" }}
              >
                <Grid container justifyContent="center">
                  <Grid item xs style={{ flexBasis: "100%" }}>
                    <Typography
                      className={classes.itemHeading}
                      title={f.name}
                      align="center"
                      style={{ wordBreak: "break-word" }}
                    >
                      {f.name}
                    </Typography>
                  </Grid>
                  <If
                    condition={
                      !!tilesetsLoaded?.find((ts) => ts.nodeId === f.id)?.loaded
                    }
                    render={() => (
                      <>
                        <Tooltip title={t("move-to").toString()}>
                          <Grid item>
                            <IconButton
                              onClick={() => handleGoToClick(f.id)}
                              size="large"
                            >
                              <MyLocationIcon className={classes.icon} />
                            </IconButton>
                          </Grid>
                        </Tooltip>
                        {tilesetsShowing?.find((ts) => ts.nodeId === f.id)
                          ?.showing ? (
                          <Tooltip title={t("hide").toString()}>
                            <Grid item>
                              <IconButton
                                onClick={() =>
                                  handleVisibilityClick(false, f.id)
                                }
                                size="large"
                              >
                                <VisibilityOffIcon className={classes.icon} />
                              </IconButton>
                            </Grid>
                          </Tooltip>
                        ) : (
                          <Tooltip title={t("show").toString()}>
                            <Grid item>
                              <IconButton
                                onClick={() =>
                                  handleVisibilityClick(true, f.id)
                                }
                                size="large"
                              >
                                <VisibilityIcon className={classes.icon} />
                              </IconButton>
                            </Grid>
                          </Tooltip>
                        )}
                      </>
                    )}
                  />
                  <Choose>
                    <Choose.When
                      condition={
                        !tsLoading &&
                        !tilesetsLoaded?.find((ts) => ts.nodeId === f.id)
                          ?.loaded
                      }
                      render={() => (
                        <Tooltip title={t("download").toString()}>
                          <Grid item>
                            <IconButton
                              onClick={() => handleDownloadClick(f)}
                              size="large"
                            >
                              <GetAppIcon className={classes.icon} />
                            </IconButton>
                          </Grid>
                        </Tooltip>
                      )}
                    />
                    <Choose.When
                      condition={tsLoading}
                      render={() => (
                        <Grid item>
                          <CircularProgress
                            size={24}
                            className={classes.loadingIndicator}
                          />
                        </Grid>
                      )}
                    />
                    <Choose.When
                      condition={
                        !tsLoading &&
                        !!tilesetsLoaded?.find((ts) => ts.nodeId === f.id)
                          ?.loaded
                      }
                      render={() => (
                        <>
                          <Tooltip title={t("remove-from-map").toString()}>
                            <Grid item>
                              <IconButton
                                onClick={() => handleRemoveClick(f.id)}
                                size="large"
                              >
                                <CloseIcon className={classes.icon} />
                              </IconButton>
                            </Grid>
                          </Tooltip>
                          <Tooltip title={t("adjust-pointcloud").toString()}>
                            <Grid item>
                              <IconButton
                                onClick={() => handleAdjustClick(f.id)}
                                size="large"
                              >
                                <TuneIcon className={classes.icon} />
                              </IconButton>
                            </Grid>
                          </Tooltip>
                        </>
                      )}
                    />
                  </Choose>
                </Grid>
              </ListItem>
            );
          })}
        </List>
      </AccordionDetails>
    </Accordion>
    // </ListItem>
  );
};

export default CesiumDrawerDataItem;
